import { useState, useEffect } from 'react'
import { Hub } from '@aws-amplify/core'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import AppBar from '@material-ui/core/AppBar'
import Typography from '@material-ui/core/Typography'
import Toolbar from '@material-ui/core/Toolbar'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import CachedIcon from '@material-ui/icons/Cached'
import CloudDoneOutlinedIcon from '@material-ui/icons/CloudDoneOutlined'
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined'
import CheckIcon from '@material-ui/icons/Check'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import ChedarLogo from '../../../../assets/images/ChedarOffline.png'
import LanguageButton from '../LanguageButton'
import { clearQuote, updateQuote } from '../../../../redux/slices/quotes'
import OnlineButton from './OnlineButton'
import OfflineButton from './OfflineButton'

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
  },
  iconImage: {
    flexGrow: 1,
  },
  logoImage: {
    [theme.breakpoints.down('xs')]: {
      height: '1rem',
    },
  },
  appbar: {
    boxShadow: 'none',
  },
  tabs: {
    marginLeft: '2rem',
    maxHeight: '35px',
    minHeight: 'unset',
    '& a': {
      minHeight: '35px',
      minWidth: '60px',
    },
  },
  activeTab: {
    backgroundColor: '#F9FAFB',
    color: '#000',
  },
  inactiveTab: {
    background: '#2e4153',
  },
  headingContainer: {
    flexGrow: 4,
    // margin: '1rem',
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'flex-end',
  },
  heading: {
    fontSize: '1.5rem',
    marginRight: '1rem',
  },
  exitButton: {
    background: '#5E6F82',
    color: '#fff',
    boxShadow: 'none',
    '&:hover': {
      background: '#495663',
    },
    '&:disabled': {
      color: '#94A3B8',
      background: '#5E6F82',
    },
    padding: '4px 8px',
    textTransform: 'unset',
    margin: 'auto 0',
  },
  saveIcon: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: '1rem',
    '& svg': {
      marginRight: '0.5rem',
    },
  },
}))

const StyledTabs = withStyles({
  indicator: {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    '& > span': {
      maxWidth: 40,
      width: '100%',
    },
  },
})(props => <Tabs {...props} TabIndicatorProps={{ children: <span /> }} />)

function LinkTab(props) {
  return <Tab component={Link} to={props.href} {...props} />
}

function GridNavbar() {
  const classes = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { quoteId } = useParams()
  const currentQuote = useSelector(state => state.quotes.currentQuote)
  const maximizeMinimize = useSelector(state => state.quotes.maximizeMinimize)
  const updateQuoteInfoDebounceApiStatus = useSelector(
    state => state.quotes.updateQuoteInfoDebounceApiStatus
  )
  const updateQuoteInfoApiCounter = useSelector(state => state.quotes.updateQuoteInfoApiCounter)
  const updateRecapQuoteInfoDebounceApiStatus = useSelector(
    state => state.quotes.updateRecapQuoteInfoDebounceApiStatus
  )
  const updateRecapQuoteInfoApiCounter = useSelector(
    state => state.quotes.updateRecapQuoteInfoApiCounter
  )
  const location = useLocation()
  const [value, setValue] = useState(0)
  const [isOnline, setIsOnline] = useState(false)
  const [isSynced, setIsSynced] = useState(false)

  useEffect(() => {
    // Waits for datastores initial sync process to complete
    setIsOnline(false)
    const removeListener = Hub.listen('datastore', async capsule => {
      const {
        payload: { event, data },
      } = capsule
      if (event === 'networkStatus' && !data.active) {
        setIsOnline(false)
      } else if (event === 'networkStatus' && data.active) {
        setIsOnline(true)
      }
      if (event === 'modelSynced' || event === 'subscriptionsEstablished') {
        setIsSynced(false)
        setIsOnline(true)
      }
      if (event === 'ready' || event === 'syncQueriesReady') {
        // Use datastore to fetch quotes
        setIsSynced(true)
      }
    })
    // dispatch(logOutUser())

    return () => {
      removeListener()
    }
  }, [])
  const [showSpinner, setShowSpinner] = useState(false)

  useEffect(() => {
    if (location.pathname.endsWith('budget')) {
      setValue('budget')
    } else if (location.pathname.endsWith('prep')) {
      setValue('prep')
    } else if (location.pathname.endsWith('wrap')) {
      setValue('wrap')
    }
  }, [location.pathname])

  useEffect(() => {
    if (showSpinner) {
    }
  }, [showSpinner])

  const handleChange = (event, newValue) => {
    setValue(newValue)
  }

  const handleExit = () => {
    if (
      updateQuoteInfoDebounceApiStatus === 'loading' ||
      updateQuoteInfoApiCounter > 0 ||
      updateRecapQuoteInfoDebounceApiStatus === 'loading' ||
      updateRecapQuoteInfoApiCounter > 0
    ) {
      setShowSpinner(true)
      setTimeout(() => {
        setShowSpinner(false)
      }, 3000)
      return
    } else {
      setShowSpinner(false)
    }
    // We are setting the users maximize minimize when exiting quote so that it is saved when they open the quote back again
    dispatch(
      updateQuote({
        quoteId,
        newQuote: { ...currentQuote, maximizeMinimize: maximizeMinimize },
      })
    )
    dispatch(clearQuote())
    navigate(`/offline`)
  }

  return (
    <>
      <AppBar position='static' className={classes.appbar}>
        <Toolbar variant='dense'>
          <div className={classes.iconImage}>
            <img src={ChedarLogo} alt='Chedar Logo' className={classes.logoImage} />
          </div>
          <div className={classes.headingContainer}>
            <Typography className={classes.heading}>{currentQuote?.name}</Typography>
            <Button
              variant='contained'
              className={classes.exitButton}
              onClick={handleExit}
              size='small'
            >
              {t('GridNavbar:exitQuote')}
              {showSpinner && <CircularProgress style={{ marginLeft: 10 }} size={20} />}
            </Button>
          </div>
          <OnlineButton />
          <LanguageButton />
        </Toolbar>
        <StyledTabs
          value={value}
          onChange={handleChange}
          aria-label='nav tabs example'
          className={classes.tabs}
        >
          <LinkTab
            className={`${value === 'budget' ? classes.activeTab : classes.inactiveTab}`}
            label='Budget'
            href={`/quote/v1/${currentQuote?.id}/budget`}
          />
          <LinkTab
            className={`${value === 'prep' ? classes.activeTab : classes.inactiveTab}`}
            label='Prep'
            href={`/quote/v1/${currentQuote?.id}/prep`}
          />
          <LinkTab
            className={`${value === 'wrap' ? classes.activeTab : classes.inactiveTab}`}
            label='Wrap'
            href={`/quote/v1/${currentQuote?.id}/wrap`}
          />
          {/* {location.pathname.endsWith('budget') && (
            <div className={classes.saveIcon}>
              {updateQuoteInfoDebounceApiStatus === 'loading' || updateQuoteInfoApiCounter > 0 ? (
                <>
                  <CachedIcon fontSize='small' /> Saving...
                </>
              ) : (
                <>
                  <CloudDoneOutlinedIcon fontSize='small' /> Saved
                </>
              )}
            </div>
          )}
          {(location.pathname.endsWith('prep') || location.pathname.endsWith('wrap')) && (
            <div className={classes.saveIcon}>
              {updateRecapQuoteInfoDebounceApiStatus === 'loading' ||
              updateRecapQuoteInfoApiCounter > 0 ? (
                <>
                  <CachedIcon fontSize='small' /> Saving...
                </>
              ) : (
                <>
                  <CloudDoneOutlinedIcon fontSize='small' /> Saved
                </>
              )}
            </div>
          )} */}
          <div className={classes.saveIcon}>
            {isOnline ? (
              <>
                {isSynced ? (
                  <>
                    <CheckIcon />
                    {t('offlineQuotes:synced')}
                  </>
                ) : (
                  <>
                    <CachedIcon fontSize='small' /> {t('offlineQuotes:syncing')}
                  </>
                )}
                {/* <CachedIcon fontSize='small' /> Saving... */}
              </>
            ) : (
              <>
                <ReportProblemOutlinedIcon style={{ color: '#F3B340' }} />
                {t('offlineQuotes:offlineGridMsg')}
                {/* <CloudDoneOutlinedIcon fontSize='small' /> Saved */}
              </>
            )}
          </div>
        </StyledTabs>
      </AppBar>
    </>
  )
}

export default GridNavbar
